import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IAutomationActionTemplateOktaAddToGroup,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { OktaGroup } from '@dadohr/shared/domainTypes/integrations/oktaApi';
import { isEqual } from 'lodash';
import { SetStateAction } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import { useOktaGroupsList } from '../../../../../../Org/pages/Integrations/integrations/okta_api/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_OKTA_ADD_TO_GROUP
} from '../../validation';

const GroupSelector = ({
  error,
  helperText,
  onBlur,
  onChange,
  value,
  groups
}: {
  error?: boolean;
  helperText?: string | null;
  onChange: (nextValue: { id: string; name: string }) => void;
  onBlur: () => void;
  value: {
    id: string;
    name: string;
  };
  groups: OktaGroup[];
}) => {
  const { t } = useUnsafeTranslation();

  const sortedOptions = groups.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <AutocompleteSingle
      fullWidth={true}
      value={value}
      label={t('Which group should the member be added to')}
      options={sortedOptions}
      getOptionLabel={(o) => o.name}
      getOptionSelected={(o, v) => o.id === v.id}
      helperText={helperText}
      error={!!error}
      required
      onChange={(groupNext) => groupNext && onChange(groupNext)}
      onBlur={() => onBlur()}
      renderOption={(o: { id: string; name: string }) => <>{o.name}</>}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => t.exec(o.name));
      }}
    />
  );
};

export const ActionSettingsOktaAddToGroup = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation
}: {
  value: IAutomationActionTemplateOktaAddToGroup;
  onChange: (nextValue: IAutomationActionTemplateOktaAddToGroup) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const [groups, loading, error] = useOktaGroupsList(org.id);

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_OKTA_ADD_TO_GROUP,
    'pplToAdd'
  );

  const validateGroup = useFieldValidator(
    setValidation,
    VALIDATORS_OKTA_ADD_TO_GROUP,
    'group'
  );

  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  const invalidGroup = validation?.fields.group || null;

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <SomethingWentWrong />;
  }

  if (!value) {
    return null;
  }

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          error={!!invalidPpltoAdd}
          expTpl={expTpl}
          fullWidth
          helperText={invalidPpltoAdd}
          label={t('Who should be added')}
          value={value.pplToAdd.filter((p) => p.type === 'actor')}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
          onChange={(nextPplToAdd) => {
            // artificially split to only contain actors, so we need to recompose
            const nextValue = {
              ...value,
              pplToAdd: nextPplToAdd.concat(
                value.pplToAdd.filter((p) => p.type === 'other')
              )
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          filterOptions={(options) =>
            options.filter((a) => !value.pplToAdd.some((p) => isEqual(p, a)))
          }
        />
      </FormRow>
      <FormRow>
        <GroupSelector
          groups={groups || EMPTY_ARR}
          error={!!invalidGroup}
          helperText={invalidGroup}
          value={value.group}
          onBlur={() => validateGroup(value, expTpl, aTpl)}
          onChange={(nextGroup) => {
            const nextValue = { ...value, group: nextGroup };
            invalidGroup && validateGroup(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
        />
      </FormRow>
    </div>
  );
};

export const ActionSettingsOktaAddToGroupReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateOktaAddToGroup;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be added')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel
          label={t('Which group should the member be added to')}
        >
          {value.group.name}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
